import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '@meno/ui/lib/api'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import { Sparkles, Wrench, Rocket, Star, X } from 'lucide-react'
import { cn } from '@meno/ui/lib/utils'

// ── Changelog data ─────────────────────────────────────────────────────────

export type ChangeEntry = { type: 'new' | 'fix' | 'improve'; key: string }

export type VersionEntry = {
  version: string
  date: string
  dateKey: string
  entries: ChangeEntry[]
  isCurrent?: boolean
}

// Single pre-release beta entry. Every feature shipped so far is compacted
// here and grouped by type (New / Improvements / Fixes) by VersionPanel.
export const CHANGELOG: VersionEntry[] = [
  {
    version: '0.9.5',
    date: 'June 24, 2026',
    dateKey: 'v095date',
    isCurrent: true,
    entries: [
      { type: 'new',     key: 'v180_playlist_details'  },
      { type: 'new',     key: 'v180_duration_tracking' },
      { type: 'new',     key: 'v180_screen_logs'       },
      { type: 'new',     key: 'v170_arabic_ui'         },
      { type: 'new',     key: 'v170_lang_toggle'       },
      { type: 'new',     key: 'v170_thmanyah_font'     },
      { type: 'new',     key: 'v160_offline_sync'      },
      { type: 'new',     key: 'v160_playlists'         },
      { type: 'new',     key: 'v150_api_client'        },
      { type: 'new',     key: 'v140_multibranch'       },
      { type: 'new',     key: 'v130_dnd_builder'       },
      { type: 'new',     key: 'v130_menu_order'        },
      { type: 'new',     key: 'v120_confirm_dialogs'   },
      { type: 'new',     key: 'v110_product_details'   },
      { type: 'new',     key: 'v110_session_mgr'       },
      { type: 'new',     key: 'v110_screen_details'    },
      { type: 'new',     key: 'v100_launch'            },
      { type: 'new',     key: 'v100_device_pairing'    },
      { type: 'new',     key: 'v100_jwt_auth'          },
      { type: 'new',     key: 'v100_screens_menus'     },
      { type: 'improve', key: 'v180_menu_media'        },
      { type: 'improve', key: 'v160_screen_detail'     },
      { type: 'improve', key: 'v160_media_lib'         },
      { type: 'improve', key: 'v160_screen_status'     },
      { type: 'improve', key: 'v150_dashboard'         },
      { type: 'improve', key: 'v150_screen_detail'     },
      { type: 'improve', key: 'v150_media_page'        },
      { type: 'improve', key: 'v140_registration'      },
      { type: 'improve', key: 'v130_menu_details'      },
      { type: 'improve', key: 'v120_android_bg'        },
      { type: 'improve', key: 'v120_screens_menus'     },
      { type: 'improve', key: 'v110_products_crud'     },
      { type: 'fix',     key: 'v180_playlist_creation' },
      { type: 'fix',     key: 'v170_rtl_layout'        },
      { type: 'fix',     key: 'v170_rtl_dialogs'       },
      { type: 'fix',     key: 'v140_device_api'        },
      { type: 'fix',     key: 'v130_db_cleanup'        },
    ],
  },
]

const COMING_SOON_KEYS = [
  'push_notifications',
  'advanced_analytics',
  'rbac_enforcement',
  'admin_mobile_app',
]

// Prefer the build-stamped app version (VITE_APP_VERSION, injected by
// scripts/viteVersionDefine.mjs from apps/frame/package.json) so the badge
// tracks the actually-shipped version on every build with no hand-edit and no
// bot commit. Falls back to the curated changelog head for local/dev builds
// where the stamp is absent.
export const CURRENT_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || CHANGELOG[0].version
export const STORAGE_KEY = 'meno_updates_seen_version'

// ── Sub-components ─────────────────────────────────────────────────────────

// Brand-aligned categories: "new" wears the WeTook red (solid tile, white
// glyph); "improve" / "fix" use semantic green / amber accents. Every value is
// either a token or carries a `dark:` variant so the rows read correctly in
// both the light and dark shells.
const TYPE_CONFIG = {
  new:     { Icon: Sparkles, tile: 'bg-primary',                            icon: 'text-primary-foreground',                labelColor: 'text-primary' },
  fix:     { Icon: Wrench,   tile: 'bg-amber-100 dark:bg-amber-950/40',     icon: 'text-amber-600 dark:text-amber-400',     labelColor: 'text-amber-600 dark:text-amber-400' },
  improve: { Icon: Star,     tile: 'bg-emerald-100 dark:bg-emerald-950/40', icon: 'text-emerald-600 dark:text-emerald-400', labelColor: 'text-emerald-600 dark:text-emerald-400' },
} as const

function EntryRow({ entry }: { entry: ChangeEntry }) {
  const { t } = useTranslation()
  const { Icon, tile, icon, labelColor } = TYPE_CONFIG[entry.type]
  const label = t(`updates.types.${entry.type}`)
  const text  = t(`updates.entries.${entry.key}`)
  return (
    <div className="flex items-start gap-3 py-2">
      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', tile)}>
        <Icon className={cn('h-3.5 w-3.5', icon)} />
      </span>
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <Badge variant="outline" className={cn('text-[10px] font-semibold uppercase tracking-wide shrink-0', labelColor)}>
          {label}
        </Badge>
        <span className="text-sm text-foreground/90">{text}</span>
      </div>
    </div>
  )
}

function VersionPanel({ entry }: { entry: VersionEntry }) {
  const { t } = useTranslation()
  const byType = (type: ChangeEntry['type']) => entry.entries.filter(e => e.type === type)
  const sections: Array<{ type: ChangeEntry['type']; labelKey: string }> = [
    { type: 'new',     labelKey: 'updates.sections.new'     },
    { type: 'improve', labelKey: 'updates.sections.improve' },
    { type: 'fix',     labelKey: 'updates.sections.fixes'   },
  ]
  return (
    <div className="flex flex-col gap-4">
      {sections.map(({ type, labelKey }) => {
        const items = byType(type)
        if (!items.length) return null
        return (
          <div key={type}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t(labelKey)}
            </p>
            <div className="divide-y divide-border/50 rounded-lg border bg-card px-3">
              {items.map(e => <EntryRow key={e.key} entry={e} />)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Admin-published changelog (overrides the static one when present) ────────
// The internal admin authors releases at /admin/whats-new; published ones are
// served here (bilingual). When the fetch is empty or fails we fall back to the
// bundled CHANGELOG above, so the modal is never blank.

type RemoteEntry = { type: string; text_en: string; text_ar: string }
type RemoteRelease = {
  id: number
  version: string | null
  title_en: string | null
  title_ar: string | null
  date_label: string | null
  entries: RemoteEntry[]
}

function RemoteEntryRow({ type, text }: { type: string; text: string }) {
  const cfg = TYPE_CONFIG[(type as keyof typeof TYPE_CONFIG)] ?? TYPE_CONFIG.new
  const { t } = useTranslation()
  const { Icon, tile, icon, labelColor } = cfg
  return (
    <div className="flex items-start gap-3 py-2">
      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', tile)}>
        <Icon className={cn('h-3.5 w-3.5', icon)} />
      </span>
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <Badge variant="outline" className={cn('text-[10px] font-semibold uppercase tracking-wide shrink-0', labelColor)}>
          {t(`updates.types.${type in TYPE_CONFIG ? type : 'new'}`)}
        </Badge>
        <span className="text-sm text-foreground/90">{text}</span>
      </div>
    </div>
  )
}

function RemoteVersionPanel({ release, ar }: { release: RemoteRelease; ar: boolean }) {
  const { t } = useTranslation()
  const pick = (e: RemoteEntry) => (ar ? e.text_ar : e.text_en) || e.text_en || e.text_ar
  const sections: Array<{ type: string; labelKey: string }> = [
    { type: 'new',     labelKey: 'updates.sections.new'     },
    { type: 'improve', labelKey: 'updates.sections.improve' },
    { type: 'fix',     labelKey: 'updates.sections.fixes'   },
  ]
  return (
    <div className="flex flex-col gap-4">
      {sections.map(({ type, labelKey }) => {
        const items = release.entries.filter(e => e.type === type)
        if (!items.length) return null
        return (
          <div key={type}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(labelKey)}</p>
            <div className="divide-y divide-border/50 rounded-lg border bg-card px-3">
              {items.map((e, i) => <RemoteEntryRow key={i} type={e.type} text={pick(e)} />)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main modal ─────────────────────────────────────────────────────────────

export default function UpdatesModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language?.startsWith('ar') ?? false

  // Admin-published changelog. Fetched once when the modal first opens; an empty
  // list or a failure leaves `remote` null so the bundled changelog shows.
  const [remote, setRemote] = useState<RemoteRelease[] | null>(null)
  useEffect(() => {
    if (!open || remote !== null) return
    let alive = true
    // Ask for THIS app's changelog. The endpoint returns the app's own
    // releases plus any marked cross-product; without the param it returns
    // every app's, which is what this modal used to show — Frame operators
    // read about Ping releases they cannot see.
    api.get('/whats-new', { params: { app: 'signage' } })
      .then(r => { if (alive) setRemote(r.data?.releases ?? []) })
      .catch(() => { if (alive) setRemote([]) })
    return () => { alive = false }
  }, [open, remote])

  function handleClose() {
    localStorage.setItem(STORAGE_KEY, CURRENT_VERSION)
    onOpenChange(false)
  }

  // Show only the LAST batch of updates, not the whole accumulated history:
  // entry keys are `vNNN_*`, so keep only those at the highest version prefix.
  const current = CHANGELOG[0]
  const verOf = (k: string) => parseInt(k.match(/^v(\d+)_/)?.[1] ?? '0', 10)
  const topVer = Math.max(0, ...current.entries.map(e => verOf(e.key)))
  const latest = { ...current, entries: current.entries.filter(e => verOf(e.key) === topVer) }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose() }}>
      <DialogContent className="max-w-3xl w-full p-0 gap-0 overflow-hidden max-h-[90vh] [&>button]:hidden">
        {/* Header — sits on the dialog surface (no black bar); the brand-blue
            icon tile is the single accent. */}
        <div className="border-b px-4 sm:px-6 py-4 sm:py-5">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary">
                <Sparkles className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-foreground text-lg truncate">
                  {t('updates.title')}
                </DialogTitle>
                <DialogDescription className="hidden sm:block text-muted-foreground text-sm mt-0.5 truncate">
                  {t('updates.subtitle')}
                </DialogDescription>
              </div>
              <Badge variant="secondary" className="hidden sm:inline-flex shrink-0">
                v{CURRENT_VERSION} · {t('updates.beta')}
              </Badge>
              <button
                onClick={handleClose}
                className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
                aria-label={t('common.close', 'Close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>
        </div>

        {/* Body: single beta changelog — scrollbar hidden */}
        <div
          className="no-scrollbar flex flex-col gap-6 overflow-y-auto p-6"
          style={{ maxHeight: 'calc(90vh - 180px)' }}
        >
          {remote && remote.length > 0
            ? remote.map(rel => <RemoteVersionPanel key={rel.id} release={rel} ar={ar} />)
            : <VersionPanel entry={latest} />}

          {/* Coming Soon */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Rocket className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">{t('updates.comingSoon')}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COMING_SOON_KEYS.map(key => (
                <div key={key} className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
                  <Rocket className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <span className="text-xs text-foreground/80">{t(`updates.soon.${key}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t bg-muted/20 px-6 py-3">
          <span className="text-xs text-muted-foreground">{t('updates.footer')}</span>
          <Button size="sm" onClick={handleClose} className="gap-1.5">
            {t('updates.gotIt')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
